// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createQueryString } from "@bg/XDL/Utilities/api";
import isApiError from "@insite/client-framework/Common/isApiError";
import {
    ApiParameter,
    del,
    get,
    patch,
    post,
    request,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import { PaginationModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

const customQuoteUri = "/api/custom/customquotes";

export interface GroupLineModel {
    lineId: string;
    productResult: ProductModel;
    productErpNumber: string;
    qty: number;
    notes: string;
    extendedPrice: string;
    sortOrder: number;
    isRelatedProduct: boolean;
    isSpecItem: boolean;
    parentId: string;
    relatedItems: GroupLineModel[];
    breakPricesAsJson: string;
    breakPricesRfqModel: any;
    customerPrice: number;
    unitOfMeasureDisplay: string;
    groupName: string;
    totalQuotePrice: number;
    margin: number;
}
export interface CustomQuoteModel {
    allowRedirectToAssociatedQuote: boolean;
    groupLinesModel: { [key: string]: GroupLineModel[] };
    name: string;
    id: string;
    customerNumber: string;
    logoFileName: string;
    expiryDate: string;
    website: string;
    groups: string;
    createdOn: string;
    modifiedOn: string;
    modifiedBy: string;
    linesCount: number;
    gstTaxValue: number;
    hstTaxValue: number;
    clientName: string;
    city: string;
    streetAddress1: string;
    streetAddress2: string;
    province: string;
    postalCode: string;
    email: string;
    phoneNumber: string;
    freightCharges: number;
    labourCharges: number;
    taxDisplayType: "NoTax" | "Gst" | "Hst";
    priceDisplayType: "ShowProductPrices" | "HideProductPrices";
    taxDisplayTypes: string[];
    priceDisplayTypes: string[];
    isCameFromNormalQuote: boolean;
    quoteIdReference: string | null;
    withSpecSheet?: boolean;
    productsInLineFormat?: boolean;
}

export interface GetQuotesCollection extends ApiParameter {
    search?: string;
    sort?: string;
    page?: number;
    pageSize?: number;
}

export interface QuotesCollectionModel {
    customQuoteCollection: CustomQuoteModel[];
    pagination: PaginationModel;
}

export interface CustomQuoteResponse {
    quotes: QuotesCollectionModel;
}

export interface CreateNewQuote extends ApiParameter {
    name: string;
}

export interface DeletCustomQuote extends ApiParameter {
    quoteId: string;
}

export interface AddQuoteToCart extends ApiParameter {
    quoteId: string;
}

export interface GetQuoteDetails extends ApiParameter {
    quoteId: string;
    sortBy?: string;
}

export interface RenameCustomQuote extends ApiParameter {
    newName: string;
    customQuoteId: string;
}

export interface AddNewGroup extends ApiParameter {
    newBlankGroupName: string;
    customQuoteId: string;
}

export interface CustomQuoteLinesModel {
    lineId: string;
    qty?: number;
    sortOrder?: number;
    price?: number;
    Note?: string;
}

export interface UpdateCustomQuoteLines extends ApiParameter {
    linesModel: CustomQuoteLinesModel[];
    updateQty?: boolean;
    updatePrice?: boolean;
    updateNote?: boolean;
    updateSortOrder?: boolean;
    updateMargin?: boolean;
    margin?: number;
}

export interface AddNewQuoteLines extends ApiParameter {
    customQuoteId: string;
    groupName: string;
    linesModel: { productErpNumber: string; qty: number; isRelatedProduct?: boolean; parentId?: string }[];
}

export interface DeleteQuoteLine extends ApiParameter {
    lineId: string;
}

export interface MoveQuoteLines extends ApiParameter {
    newGroupName: string;
    linesModel: { lineId: string; sortOrder?: number }[];
}

export interface CopyQuoteLines extends ApiParameter {
    newGroupName: string;
    linesIds: string[];
}

export interface DeleteQuoteGroup extends ApiParameter {
    customQuoteId: string;
    groupName: string;
}

export interface RenameGroup extends ApiParameter {
    customQuoteId: string;
    originalGroupName: string;
    newGroupName: string;
}

export interface RequestQuote extends ApiParameter {
    quoteId: string;
}

export async function getCustomQuotes(params: GetQuotesCollection) {
    try {
        const timestamp = `time${new Date().getTime()}`;
        const query = createQueryString([
            `page=${params.page || 1}`,
            `${params.pageSize ? `pageSize=${params.pageSize}` : ""}`,
            `${params.search ? `search=${params.search}` : ""}`,
            `${params.sort ? `sort=${params.sort}` : ""}`,
            timestamp,
        ]);

        const quotes = await get<CustomQuoteResponse>(`${customQuoteUri}${query}`);
        return {
            successful: true,
            quotes,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function createCustomQuote(params: CreateNewQuote) {
    try {
        const quote = await post<any>(`${customQuoteUri}/CreateNew`, params);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function deleteCustomQuote(params: DeletCustomQuote) {
    try {
        const quote = await del(`${customQuoteUri}/${params.quoteId}`);
        return {
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                error: (error as any).errorMessage,
            };
        }
        return {
            successful: true,
        };
    }
}

export async function addQuoteToCart(params: AddQuoteToCart) {
    try {
        const cart = await post<any>(`${customQuoteUri}/AddToCart/${params.quoteId}`);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function getQuoteDetails(params: GetQuoteDetails) {
    try {
        const timestamp = `time${new Date().getTime()}`;
        const query = createQueryString([params.sortBy ? `sortBy=${params.sortBy}` : "", timestamp]);

        const quote = await get<CustomQuoteModel>(`${customQuoteUri}/${params.quoteId}${query}`);
        return {
            successful: true,
            quote,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function renameCustomQuote(params: RenameCustomQuote) {
    try {
        const quote = await patch<any>(`${customQuoteUri}/Rename`, params);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function addNewGroup(params: AddNewGroup) {
    try {
        const quote = await post<AddNewGroup>(`${customQuoteUri}/AddNewBlankGroup`, params);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function updateQuoteLine(params: UpdateCustomQuoteLines) {
    try {
        const quote = await patch<any>(`${customQuoteUri}/UpdateLineInfo`, params);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function addNewLine(params: AddNewQuoteLines) {
    try {
        const quote = await post<any>(`${customQuoteUri}/AddNewItem`, params);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function deleteQuoteLine(params: DeleteQuoteLine) {
    try {
        const quote = await del(`${customQuoteUri}/DeleteItem/${params.lineId}`);
        return {
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                error: (error as any).errorMessage,
            };
        }
        return {
            successful: true,
        };
    }
}

export async function moveQuoteLines(params: MoveQuoteLines) {
    try {
        const quote = await patch<any>(`${customQuoteUri}/move`, params);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function copyQuoteLines(params: CopyQuoteLines) {
    try {
        const quote = await patch<any>(`${customQuoteUri}/copy`, params);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function deleteQuoteGroup(params: DeleteQuoteGroup) {
    try {
        const quote = await del(`${customQuoteUri}/${params.customQuoteId}/DeleteGroup/${params.groupName}`);
        return {
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                error: (error as any).errorMessage,
            };
        }
        return {
            successful: true,
        };
    }
}

export async function renameGroup(params: RenameGroup) {
    try {
        const quote = await patch<any>(`${customQuoteUri}/RenameGroup`, params);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export async function convertToEpiQuote(params: RequestQuote) {
    try {
        const quote = await post<any>(`${customQuoteUri}/RequestQuote/${params.quoteId}`);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: (error as any).errorMessage,
        };
    }
}

export interface UploadLogoParams {
    formData: any;
    customQuoteId: string;
}

export async function uploadLogo(parameter: UploadLogoParams): Promise<ServiceResult<any>> {
    try {
        const result = await request<any>(
            `${customQuoteUri}/UploadLogo/${parameter.customQuoteId}`,
            "POST",
            {},
            parameter ? (parameter.formData as any) : undefined,
        );
        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error?.errorJson?.message,
            };
        }
        throw error;
    }
}

export interface DeleteLogoParams {
    customQuoteId: string;
}

export async function deleteLogo(parameter: DeleteLogoParams): Promise<ServiceResult<any>> {
    try {
        const result = await request<any>(
            `${customQuoteUri}/DeleteLogo/${parameter.customQuoteId}`,
            "POST",
        );
        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error?.errorJson?.message,
            };
        }
        throw error;
    }
}